import './ViewDialog.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import { EntityIdRouteParams } from '../../../../../types/routing';
import DialogSpinner from '../../../../Dialogs/DialogSpinner/DialogSpinner';
import { LogisticsRoute, getPointTypes } from '../../LogisticsRoutes.functions';
import GeoLocation from './GeoLocation';

type Props = {
  isShown: boolean;
  onHide: () => void;
  isEditBtnShown: boolean;
  setIsEditDialog: (isEditDialog: LogisticsRoute) => void;
};

function ViewDialog({
  isShown,
  onHide,
  isEditBtnShown,
  setIsEditDialog,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const pointTypes = useMemo(() => getPointTypes(t), [t]);
  const findType = useCallback(
    (pointTypeId) =>
      pointTypes.find((type) => type.value == pointTypeId)?.label,
    [pointTypes]
  );

  const { data, isLoading } = useAxiosHook<LogisticsRoute>(
    `/logistics-route/${id}`,
    { skipWhen: !isShown && !id }
  );

  const routeMap = useMemo(() => {
    if (!data?.points?.length) return null;

    return data.points
      .sort((a, b) => a.sequence - b.sequence)
      .map((item) => {
        const loc = item.address?.location;
        if (!loc) return null;

        const lat = loc.lat;
        const lng = loc.long;

        if (lat == null || lng == null) return null;

        return [lat, lng];
      })
      .filter((coords): coords is [number, number] => coords !== null);
  }, [data]);

  const dialogFooter = !isLoading && (
    <>
      {isEditBtnShown && (
        <Button
          label={t('Edit')}
          className="p-button-text"
          onClick={() => setIsEditDialog(data!)}
        />
      )}
      <Button label={t('Close')} onClick={() => onHide()} />
    </>
  );

  return (
    <Dialog
      header={isLoading ? t('Loading...') : t('Preview on route')}
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 580, maxWidth: '100%' }}
      className="logistics-routes-view-dialog"
    >
      {isLoading ? (
        <DialogSpinner />
      ) : (
        <>
          <span className="section-title">{t('General information')}</span>
          <table cellPadding="0" cellSpacing="0">
            <tbody>
              <tr>
                <td>{t('Route name')}</td>
                <td className="route-name">{data?.name}</td>
              </tr>
            </tbody>
          </table>
          <span className="section-title">{t('Transporters')}</span>
          {data?.transporters && data?.transporters.length > 0 ? (
            <table cellPadding="0" cellSpacing="0">
              <tbody>
                {data?.transporters.map((item) => (
                  <tr key={item.id}>
                    <td>{t('Name')}</td>
                    <td>{item.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span>{t('There is no transporters.')}</span>
          )}
          <span className="section-title">{t('Points')}</span>
          {data?.points && data?.points.length > 0 ? (
            <table cellPadding="0" cellSpacing="0">
              <thead>
                <tr>
                  <th>{t('Sequence')}</th>
                  <th>{t('Name')}</th>
                  <th>{t('Timeframe')}</th>
                  <th>{t('Point Type')}</th>
                </tr>
              </thead>
              <tbody>
                {data?.points.map((item) => (
                  <tr key={item.entityId}>
                    <td>{item.sequence}</td>
                    <td>{item.pointName}</td>
                    <td>{item.maintenanceTimeframe}</td>
                    <td>{findType(item.pointTypeId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span>{t('There is no routes.')}</span>
          )}
          <span className="section-title">{t('Map')}</span>
          {data?.points && data?.points.length > 0 ? (
            <GeoLocation data={routeMap} />
          ) : (
            <span>{t('There is no points.')}</span>
          )}
        </>
      )}
    </Dialog>
  );
}

export default ViewDialog;
